import { Button, Dialog, DialogContent, DialogTrigger, IconBin, IconCreate, IconUser, Text } from "@sentricmusic/ui";
// Value imports
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetMyWriters } from "@/api/queries";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import type { WriterForSelection } from "../../components";

import { CreateWriterModal } from "../../components";
import SelectExistingWritersModal from "../components/SelectExistingWritersModal";
import useMultiSongStore from "../store/multiSongStore";
import useWriterStore from "../store/writerStore";

export default function ChooseWritersStep() {
  const { t } = useTranslation("album");
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const [editingWriterCode, setEditingWriterCode] = useState<string | null>(null);

  const selectedAccount = useAppStore(s => s.selectedAccount);
  const { tracks } = useMultiSongStore();
  const {
    selectedWriters,
    removeSelectedWriter,
    setAvailableWriters,
    addSelectedWriter,
  } = useWriterStore();

  // Get auxiliary writer attachments - map writercode to array of song titles
  const auxiliaryWriterAttachments = useMemo(() => {
    const attachments = new Map<string, string[]>();

    tracks.forEach((track) => {
      if (track.auxiliaryWriters && track.auxiliaryWriters.length > 0) {
        track.auxiliaryWriters.forEach((writerCode) => {
          const existing = attachments.get(writerCode) || [];
          attachments.set(writerCode, [...existing, track.name]);
        });
      }
    });

    return attachments;
  }, [tracks]);

  // Fetch available writers from API
  const writersQuery = useGetMyWriters({ aac: selectedAccount as string });

  // Update available writers in store when query succeeds
  useEffect(() => {
    if (writersQuery.data?.writers) {
      setAvailableWriters(writersQuery.data.writers);
    }
  }, [writersQuery.data, setAvailableWriters]);

  // Auto-add auxiliary writers to selectedWriters when component mounts
  useEffect(() => {
    if (writersQuery.data?.writers && auxiliaryWriterAttachments.size > 0) {
      const auxiliaryWriterCodes = Array.from(auxiliaryWriterAttachments.keys());

      auxiliaryWriterCodes.forEach((writerCode) => {
        // Check if writer is not already selected
        const alreadySelected = selectedWriters.some(w => w.writercode === writerCode);
        if (!alreadySelected) {
          // Find writer details from available writers
          const writerDetails = writersQuery.data.writers.find(w => w.writercode === writerCode);
          if (writerDetails) {
            const albumWriter = {
              writercode: writerDetails.writercode,
              writername: writerDetails.writername ?? `${writerDetails.firstname ?? ""} ${writerDetails.lastname ?? ""}`.trim(),
              caeipinumber: writerDetails.caeipinumber ?? undefined,
            };
            addSelectedWriter(albumWriter);
          }
        }
      });
    }
  }, [writersQuery.data, auxiliaryWriterAttachments, selectedWriters, addSelectedWriter]);

  // Adapter function to handle writer creation from the agnostic modal
  const handleWriterCreated = (writer: WriterForSelection) => {
    // Convert WriterForSelection to the album's SelectedWriter format
    const albumWriter = {
      writercode: writer.writercode,
      writername: writer.writername,
      caeipinumber: writer.caeipinumber ?? undefined,
    };

    addSelectedWriter(albumWriter);
    setIsCreateModalOpen(false);
    setEditingWriterCode(null);
  };

  // Adapter function to handle writer updates from the agnostic modal
  const handleWriterUpdated = (writer: WriterForSelection) => {
    // Remove the old writer and add the updated one
    if (editingWriterCode != null && editingWriterCode.trim() !== "") {
      removeSelectedWriter(editingWriterCode);
    }

    const albumWriter = {
      writercode: writer.writercode,
      writername: writer.writername,
      caeipinumber: writer.caeipinumber ?? undefined,
    };

    addSelectedWriter(albumWriter);
    setIsCreateModalOpen(false);
    setEditingWriterCode(null);
  };

  // Sort writers: regular writers first, then auxiliary writers
  const sortedWriters = useMemo(() => {
    const regular = selectedWriters.filter(w => !auxiliaryWriterAttachments.has(w.writercode));
    const auxiliary = selectedWriters.filter(w => auxiliaryWriterAttachments.has(w.writercode));
    return { regular, auxiliary };
  }, [selectedWriters, auxiliaryWriterAttachments]);

  return (
    <div className={`space-y-6 ${isTunecore ? "text-white p-4 rounded-lg bg-[#282828]" : "bg-sentric-light-grey p-4 rounded-lg text-old-sentric-dark-gray"}`} id="albumChooseWritersStep">
      {/* Header with buttons */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col items-center justify-between w-full gap-3 md:flex-row">
          <Dialog open={isSelectModalOpen} onOpenChange={setIsSelectModalOpen}>
            <DialogTrigger className="w-full md:w-1/2" asChild>
              <Button
                icon={<IconUser className={`${isTunecore ? "stroke-white" : "stroke-black"}`} />}
                variant="primary"
                label={t("album:add-existing-writers") ?? "Add Existing Writers"}
              />
            </DialogTrigger>
            <DialogContent className="max-w-4xl">
              <SelectExistingWritersModal onClose={() => setIsSelectModalOpen(false)} />
            </DialogContent>
          </Dialog>

          <Dialog
            open={isCreateModalOpen}
            onOpenChange={(open) => {
              setIsCreateModalOpen(open);
            }}
          >
            <DialogTrigger className="w-full md:w-1/2" asChild>
              <Button
                icon={<IconCreate className={`${isTunecore ? "stroke-white" : "stroke-black"}`} />}
                variant="primary"
                label={t("album:create-new-writer") ?? "Create New Writer"}
              />
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <CreateWriterModal
                onWriterCreated={handleWriterCreated}
                onWriterUpdated={handleWriterUpdated}
                onClose={() => {
                  setIsCreateModalOpen(false);
                  setEditingWriterCode(null);
                }}
                editingWriterCode={editingWriterCode}
                availableWriters={writersQuery.data?.writers.map(writer => ({
                  writercode: writer.writercode,
                  writername: writer.writername,
                  caeipinumber: writer.caeipinumber,
                  dateofbirth: writer.dateofbirth,
                })) ?? []}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Selected writers list */}
      <div className="space-y-4">

        {/* Writers list */}
        {selectedWriters.length === 0 ? (
          <div className="py-8 text-center border rounded-lg border-sentric-mid-grey">
            <Text as="p">
              {t("album:no-writers-selected-use-buttons") ?? "No writers selected. Use the buttons above to add writers for this album."}
            </Text>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Regular Writers Section - only show heading if there are regular writers */}
            {sortedWriters.regular.length > 0 && (
              <Text as="subtitle">
                {t("album:main-writers") ?? "Main Writers"}
              </Text>
            )}

            {/* No main writers message */}
            {sortedWriters.regular.length === 0 && sortedWriters.auxiliary.length > 0 && (
              <div className={`p-4 transition-colors rounded-lg ${isTunecore ? "bg-tunecore-grey-element" : "bg-white border border-sentric-mid-grey"}`}>
                <Text as="p" className="text-sm ">
                  {t("album:no-main-writers-add-above") ?? "No main writers added yet"}
                </Text>
              </div>
            )}

            {sortedWriters.regular.map(writer => (
              <div
                key={writer.writercode}
                className={`p-4 transition-colors rounded-lg ${isTunecore ? "bg-tunecore-grey-element" : "bg-white border border-sentric-mid-grey"}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <Text as="p" className="font-semibold">
                      {writer.writername}
                    </Text>
                    <Text as="p" className="mt-1 text-sm">
                      {t("album:cae-ipi-label")}
                      :
                      {" "}
                      {writer.caeipinumber ?? t("album:n-a")}
                    </Text>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      tooltip
                      tooltipText={t("album:remove-writer") ?? "Remove Writer"}
                      variant="link"
                      onClick={() => removeSelectedWriter(writer.writercode)}
                      icon={<IconBin className={`${isTunecore ? "stroke-white group-hover:stroke-tunecore-green" : "stroke-black"}`} />}
                      className="group hover:scale-110"
                      title={t("album:remove-writer") ?? "Remove writer"}
                    />
                  </div>
                </div>
              </div>
            ))}

            {/* Auxiliary Writers Section - compact, locked cards */}
            {sortedWriters.auxiliary.length > 0 && (
              <>
                {sortedWriters.auxiliary.map(writer => (
                  <div
                    key={writer.writercode}
                    className={`p-2 transition-colors rounded-lg opacity-75 ${isTunecore ? "bg-tunecore-grey-element" : "bg-sentric-very-light-grey border border-sentric-mid-grey"}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Text as="p" className="text-sm font-medium truncate">
                            {writer.writername}
                          </Text>
                          {(writer.caeipinumber != null) && writer.caeipinumber.trim() !== "" && (
                            <Text as="p" className="text-xs">
                              {writer.caeipinumber}
                            </Text>
                          )}
                        </div>
                        <Text as="p" className="mt-0.5 text-xs truncate">
                          <span className="font-semibold">Attached to:</span>
                          {" "}
                          {auxiliaryWriterAttachments.get(writer.writercode)!.join(", ")}
                        </Text>
                      </div>
                      <div className="flex items-center flex-shrink-0 gap-2">
                        <Button
                          tooltip
                          tooltipText={t("album:cannot-remove-song-writer") ?? "Cannot remove - attached to specific songs"}
                          variant="link"
                          onClick={undefined}
                          icon={<IconBin className="opacity-30 stroke-sentric-grey-text2" />}
                          className="cursor-not-allowed"
                          title={t("album:cannot-remove-song-writer") ?? "Cannot remove - attached to specific songs"}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
